/**
 * 对话框焦点管理（M5-02 / DESIGN.md §7）。
 *
 * 打开时焦点自动移入对话框内第一个可聚焦控件，Tab/Shift+Tab 在对话框内
 * 循环（到最后一个控件再 Tab 回到第一个，反向同理），关闭时把焦点还给
 * 打开它的那个控件——这是 WCAG 2.1 "Focus Order"/"No Keyboard Trap" 两条
 * 一起要求的行为：陷进对话框是刻意的（不然键盘用户 Tab 出去会跑到背后
 * 被遮住的画布/工具栏），但陷住之后必须能回到打开前的状态。
 *
 * 顺带补上 Esc 关闭：审计这批对话框发现 `main.tsx` 里全部 `Escape` 分支
 * 都是变换/裁剪/钢笔这类工具会话的取消逻辑，没有一处是给这些 modal
 * 对话框用的——backdrop 只接了 `onClick`，纯键盘用户根本关不掉。既然
 * 这里已经在管这批对话框的键盘事件，顺手把 Esc 也接上，不单开一个
 * 平行的机制。
 *
 * 这里的对话框组件全都是"实例贯穿应用生命周期，`<Show when={props.open}>`
 * 控制显隐"这个模式（不是每次打开都重新 mount 整个组件）——`onMount`/
 * `onCleanup` 只在组件第一次创建时跑一次，跟"每次打开都要重新捕获/归还
 * 焦点"对不上。所以焦点逻辑包在 `createEffect` 里，跟着 `open()` 信号的
 * 每一次变化各自跑一遍 setup/teardown，而不是组件级的一次性生命周期。
 *
 * `container()` 在 effect 同步执行的这一刻可能还是 null——`<Show>` 内部
 * 挂载 DOM（进而触发 `ref` 回调设置 `container` 的返回值）是不是发生在
 * 本 effect 之前，Solid 并不保证顺序。用 `queueMicrotask` 把真正读
 * container/挪焦点这一步推迟到本轮同步渲染结束之后，规避这个时序问题。
 *
 * 真实踩过的坑：`ColorPickerDialog` 关闭时（`onConfirm` 里）会先同步调用
 * `textController.refocus()`——把焦点带回正在编辑的文本框（选中一段文字
 * 改颜色，确定后应该继续编辑，不是把焦点扔回打开取色器的那个色块按钮）。
 * 这发生在 `onClose()`（进而 `open()` 变 false）**之前**，但本 hook 的
 * `onCleanup` 无条件把焦点归还给 `previouslyFocused`（那个色块按钮），
 * 会在 `refocus()` 刚放好焦点之后又把它抢回来，表现为"选中文字改完颜色，
 * Ctrl+Enter 提交不了"（键盘事件全去了色块按钮，没人听）。用 `focusin`
 * 监听器实时侦测："对话框还开着的时候，焦点被挪到了 `attachedTo` 以外"
 * 这件事一旦发生就不再是本 hook 该管的了——应用代码自己的焦点决定优先，
 * `onCleanup` 里不再抢，也不依赖"`<Show>` unmount DOM"和"`onCleanup` 跑"
 * 谁先谁后这种没有保证的时序（`focusin` 是挪焦点那一刻同步触发的，不受
 * 卸载顺序影响）。
 */
import { createEffect, onCleanup } from 'solid-js';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null || el === document.activeElement;
}

function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
}

/**
 * @param open 对话框是否处于打开状态（信号读取函数）。
 * @param container 对话框根元素（信号读取函数，可能暂时为 null）。
 * @param onEscape Esc 键的处理——通常直接传对话框的 `onClose`。省略时 Esc
 *   不做任何事（极少数对话框自己另有 Esc 语义，比如一个正在编辑中的
 *   取消，才需要省略这个参数、自己接管）。
 */
export function useFocusTrap(
  open: () => boolean,
  container: () => HTMLElement | null,
  onEscape?: () => void,
): void {
  createEffect(() => {
    if (!open()) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    let cancelled = false;
    let attachedTo: HTMLElement | null = null;
    /** 对话框还开着的时候，应用代码是否已经主动把焦点挪到了 `attachedTo`
     * 以外——一旦发生，`onCleanup` 就不该再帮倒忙抢回来。 */
    let focusEscaped = false;

    const onFocusIn = (event: FocusEvent): void => {
      if (attachedTo === null) return;
      const target = event.target;
      if (target instanceof Node && !attachedTo.contains(target)) focusEscaped = true;
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (attachedTo === null) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusableIn(attachedTo);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !attachedTo.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !attachedTo.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    queueMicrotask(() => {
      if (cancelled) return;
      const el = container();
      if (el === null) return;
      attachedTo = el;
      document.addEventListener('focusin', onFocusIn);
      const first = focusableIn(el)[0] ?? el;
      first.focus();
      el.addEventListener('keydown', onKeyDown);
    });

    onCleanup(() => {
      cancelled = true;
      attachedTo?.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      // 应用代码已经把焦点主动挪去了别处（见文件头注释的 refocus() 例子）
      // 就不再抢——那次挪动才是最终决定。否则按原逻辑归还给打开它的控件：
      // 焦点可能已经因为用户点别处而移开了，只在它还挂在文档里（原触发
      // 元素没被卸载）时才抢回来
      if (!focusEscaped && previouslyFocused !== null && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    });
  });
}
